import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { navigate } from '@gatsbyjs/reach-router';
// Components
import Layout, { TwoThirds, SideBar, Container } from '../../components/layout';
import { ContentWithSideBar } from '../../components/styled/containers';
import PageTitle from '../../components/page-title';
import RadioButtonGroup from '../../components/input-fields/radio-button-group';
import Pagination from '../../components/pagination';
import SuggestionsList from '../../components/search/suggestions-list';
import BestBet from '../../components/search/best-bet';
import ResultItem from '../../components/search/result-item';
import SearchBox from '../../components/search-box';
import NoResults from '../../components/search/no-result';
import LinkHandler from '../../components/link-handler';
import LoadingBar from '../../components/loading-bar';
import Page from '../../components/page';
import GatsbyHead from '../../components/layout/gatsby-head';
import { useSiteConfig } from '../../hooks/site-config';

// Utils
import { extractLayoutProps } from '../../utils/layout';

import {
  buildSearchQuery,
  fetchSearchResults,
  parseResults,
} from '../../components/search/helpers';
import { getQueryParams } from '../../utils/query-params';
import { ALL } from '../../components/search/constants';
import { sendEvent } from '../../utils/analytics';
// Styles
import {
  FilterSubheading,
  HorizontalRule,
  ResultsList,
  LegalRedirectWrapper,
} from '../../components/search/styles';
import Tabs from '../../components/tabs';

const SearchResultsPagePageQuery = graphql`
  query SearchResultsPagePageQuery {
    ...NavigationFragment
  }
`;

const SearchResultsPage = ({ location }) => {
  const [results, setResults] = useState();
  const [loading, setLoading] = useState();
  const [error, setError] = useState();
  const { contentfulConfig } = useStaticQuery(SearchResultsPagePageQuery);

  const urlParams = useMemo(
    () => getQueryParams(location.search),
    [location.search]
  );

  const query = urlParams?.query;

  const [
    total,
    filters,
    section,
    spellingSuggestion,
    items,
    pagination,
    suggestions,
    bestBet,
  ] = parseResults(results);

  const handleSearch = async (path, updateUrl = true) => {
    setLoading(true);
    try {
      setResults(await fetchSearchResults(path));
      setLoading(false);
      setError(false);
    } catch (e) {
      setError(true);
    }

    if (updateUrl) {
      navigate(path);
    }
  };

  const handleFilterChange = (selectedSection) => {
    const path = buildSearchQuery({
      query,
      section: selectedSection === ALL ? null : selectedSection,
    });

    sendEvent({
      event: 'filterSearchResults',
      filterType: selectedSection,
    });

    navigate(path);
  };

  const titleSuffix = `${useSiteConfig().title}`;
  useEffect(() => {
    let title = `Search - ${titleSuffix}`;

    if (query) {
      // Without a query “Search - Shelter {country}“
      // With a query “{query} - Search - Shelter {country}“
      if (query) {
        title = `${query} - Search - ${titleSuffix}`;
      }

      // With pagination “{query} - Search - Shelter {country} - page {pagination.current} of {pagination.total}“
      if (pagination && pagination.total > 1) {
        title = `${query} - Search - page ${pagination.current} of ${pagination.total} - ${titleSuffix}`;
      }

      document.title = title;
    } else {
      document.title = `${title}`;
    }

    return () => {
      document.getElementsByTagName('title')[0].remove();
    };
  }, [location.search, query, titleSuffix, pagination]);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (location.search) {
      handleSearch(location.search, false);
    }
  }, [location.search]);

  // Search filtering uses different components on the england/shelter sites
  // This swaps out the filtering component while allowing us to keep
  // the page logic the same
  const filterInput = useMemo(() => {
    if (!filters) {
      return null;
    }

    if (process.env.SHELTER_SITE === 'scotland') {
      const tabFilters = filters.filter(({ count }) => count > 0);

      return section === 'legal' ? (
        <LegalRedirectWrapper>
          <p>
            This search only includes <strong>Shelter Legal</strong>
          </p>
          <p>
            <LinkHandler
              link={{
                internal: true,
                path: buildSearchQuery({
                  query,
                }),
              }}
            >
              Search all pages on Shelter
            </LinkHandler>
          </p>
        </LegalRedirectWrapper>
      ) : (
        <Tabs
          name="filterSearchResults"
          defaultSelected={section}
          options={tabFilters}
          onChange={({ value }) => handleFilterChange(value)}
          controls="results"
          anchor="results"
          headingText="Filter results"
          headingId="tabs-filter-heading"
          descText="Switch between tabs to filter your search results"
          descId="tabs-filter-desc"
        />
      );
    }

    const radioFilters = filters?.map((filter) => ({
      ...filter,
      label: `${filter.label} (${filter.count})`,
    }));

    return (
      <RadioButtonGroup
        id="filterSearchResults"
        name="filterSearchResults"
        value={section || ALL}
        options={radioFilters}
        onChange={(event) => handleFilterChange(event.currentTarget.value)}
        boldWhenChecked
        legend={<FilterSubheading>Filter results</FilterSubheading>}
      />
    );
  }, [process.env.SHELTER_SITE, section, filters, handleFilterChange]);

  let subHeading;

  if (!urlParams) {
    return null;
  }

  if (error) {
    subHeading = <>Sorry there was an error loading results for "{query}"</>;
  } else if (!results) {
    subHeading = <>Results loading for "{query}"</>;
  } else if (total) {
    subHeading = (
      <>
        Found {total} results for "{query}"
      </>
    );

    sendEvent({
      event: 'searchComplete',
      total,
    });
  } else if (query) {
    subHeading = <>No results found for "{query}"</>;

    sendEvent({
      event: 'noSearchResultsFound',
      spellingSuggestion,
    });
  }

  let divider = null;

  if (query) {
    if (loading) {
      divider = <LoadingBar />;
    } else {
      divider = <HorizontalRule />;
    }
  }

  const resultWrapperProps =
    process.env.SHELTER_SITE === 'scotland'
      ? {
          id: 'results',
          role: 'tabpanel',
          'aria-labelledby': `tab-${section}`,
        }
      : null;

  return (
    <Page
      pageContext={{ title: 'Search', internal: { type: 'search' } }}
      path="/search"
    >
      <Layout {...extractLayoutProps({}, contentfulConfig)}>
        <article>
          <PageTitle twoThirds>
            <h1>Search results</h1>
          </PageTitle>
          <Container>
            <TwoThirds>
              <SearchBox
                placeholder="Search by keyword with filters"
                sectionToSearch={section === ALL ? null : section}
                query={query}
                submitCallback={handleSearch}
              />
              {results?.total ? filters && filterInput : null}
            </TwoThirds>
          </Container>
          {divider}
          <Container>
            <ContentWithSideBar {...resultWrapperProps}>
              <TwoThirds>
                {total ? (
                  <>
                    <h2>{subHeading}</h2>
                    {bestBet ? (
                      <BestBet {...bestBet} searchTerm={query} />
                    ) : null}

                    <ResultsList>
                      {items &&
                        items.map((props, i) => (
                          <ResultItem key={i} {...props} searchTerm={query} />
                        ))}
                    </ResultsList>
                    {pagination && pagination.total > 1 && (
                      <Pagination {...pagination} />
                    )}
                  </>
                ) : null}

                {!total && query && !loading ? (
                  <>
                    <h2>{subHeading}</h2>
                    <NoResults
                      spellingSuggestion={spellingSuggestion}
                      section={section === ALL ? null : section}
                    />
                  </>
                ) : null}
              </TwoThirds>
              <SideBar desktop>
                {suggestions?.length ? (
                  <>
                    <h3>Search suggestions:</h3>
                    <SuggestionsList
                      section={section === ALL ? null : section}
                      suggestions={suggestions}
                      searchTerm={query}
                    />
                  </>
                ) : null}
              </SideBar>
            </ContentWithSideBar>
          </Container>
        </article>
      </Layout>
    </Page>
  );
};

SearchResultsPage.propTypes = {
  location: PropTypes.object,
};

export default SearchResultsPage;
